import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Table from 'components/widgets/Table';
import {
    makeStyles, Dialog, DialogContent, AppBar, Toolbar, Typography, Button,
    useMediaQuery,
} from '@material-ui/core';
import clsx from 'clsx';
import { useTheme } from '@material-ui/core/styles';
import AccountingStyles from 'styles/modules/accounting/AccountingStyles';
import Header from 'components/widgets/Header';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import InputSearch from 'components/widgets/InputSearch';
import DialogActions from 'components/widgets/modal/DialogActions';
import Split from 'react-split';
import { AddIcon } from 'components/icons';
import TableUtils from 'utils/TableUtils';
import VinScanner from 'components/widgets/inventory/VinScanner';
import VehiclesInCards from 'components/modules/service/serviceInvoice/create/VehiclesInCards';
import CustomerPanel from 'components/modules/service/serviceInvoice/create/CustomerPanel';
import useCustomerAndVehicleMethods from 'components/modules/service/serviceInvoice//hooks/useCustomerAndVehicleMethods';

const useStyle = makeStyles((theme) => AccountingStyles.checkActionList(theme));
const ownStyle = makeStyles((theme) => (
    {
        dialogContent: {
            height: '62vh',
            '& > div:first-child': {
                height: '62vh',
                [theme.breakpoints.down('sm')]: {
                    height: '100%',
                },
            },
            padding: 0,
            overflow: 'hidden',
        },
        mainTable: {
            height: 'calc(100vh - 420px)',
            paddingLeft: '5px',
            paddingRight: '5px',
            '& div.rt-table div.rt-th': {
                backgroundColor: theme.palette.background.white,
            },
        },
        search: {
            borderRadius: 10,
            width: 350,
            '& .form-control': {
                height: 'calc(1.5em + 0.5rem + 2px)',
                padding: '0.25rem 0.5rem',
                fontSize: '0.875rem',
                lineHeight: '1.5',
                borderRadius: '0.2rem',
            },
        },
        buttonSave: {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.text.white,
            '&:hover': {
                backgroundColor: theme.palette.success.dark,
            },
        },
        customerSection: {
            [theme.breakpoints.down('sm')]: {
                '& > div > div': {
                    width: '100%',
                    marginTop: '5px',
                    marginBottom: '5px',
                    '& > button': {
                        marginTop: '5px',
                    },
                },
            },
        },
        scanner: {
            display: 'flex',
            '& > div:nth-child(1) > div input': {
                height: '36px',
            },
        },
    }
));

const CustomerForServiceList = (props) => {
    const classes = {
        ...useStyle(),
        ...ownStyle(),
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        appBar, centerItems, title, dialogContent, search,
    } = classes;

    const {
        inHouse, onClose, onSelectCustomer,
        showAddNewCustomerButton, onClickNewCustomerButton,
        onClickNewVehicleButton, showVehicleGrid,
    } = props;

    const {
        getCustomerData, loadingCustomerInfo, records, getCustomerColumns,
        getVehicleData, loadingVehicleInfo,
        vehicles, getVehicleColumns, clearCustomers,
    } = useCustomerAndVehicleMethods({ inHouse });

    const [selectedRecord, selectCustomer] = useState(null);
    const [selectedVehicle, onSelectedVehicle] = useState(null);
    const [searchData, setSearchData] = useState({
        key: TableUtils.generateUUID(),
        search: '',
    });

    const onSelectedRecord = (value) => {
        selectCustomer(value);
        onSelectedVehicle(null);
    };

    const onSearch = (value) => {
        if (!inHouse && (!value || value?.length <= 3)) return;

        clearCustomers();
        getCustomerData({
            variables: {
                inHouse,
                search: value,
            },
        });
    };

    const onSearchVehicle = (value) => {
        const { firstName = '', lastName = '', customerCode = 0 } = selectedRecord || {};

        setSearchData({
            key: TableUtils.generateUUID(),
            search: value,
        });

        getVehicleData({
            variables: {
                code: inHouse ? `${firstName} ${lastName}` : customerCode,
                inHouse,
                search: value?.trim() ?? '',
            },
        });
    };

    useEffect(() => {
        if (inHouse) {
            getCustomerData({
                variables: {
                    inHouse,
                    search: '',
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedRecord?.customerCode > 0) {
            const { firstName, lastName, customerCode } = selectedRecord;
            getVehicleData({
                variables: {
                    code: inHouse ? `${firstName} ${lastName}` : customerCode,
                    inHouse,
                    search: '',
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRecord?.customerCode]);

    return (
        <>
            <Dialog
                open
                onClose={onClose}
                maxWidth={inHouse ? 'lg' : 'md'}
                fullScreen={isMobile}
                fullWidth={!isMobile}
                disableBackdropClick
                disableEscapeKeyDown
                scroll="paper"
            >
                <AppBar className={appBar}>
                    <Toolbar className={centerItems}>
                        <Typography variant="h6" className={title}>
                            {showVehicleGrid ? 'Select customer and vehicle' : 'Select customer'}
                        </Typography>
                        <div className={centerItems}>
                            <IconButton edge="start" color="inherit" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </Toolbar>
                </AppBar>
                <DialogContent className={dialogContent}>
                    {showVehicleGrid ? (
                        <Split
                            sizes={showVehicleGrid ? [50, 50] : [100, 0]}
                            className={classes.split}
                            direction="vertical"
                            minSize={0}
                        >
                            <CustomerPanel
                                isMobile={isMobile}
                                search={search}
                                classes={classes}
                                inHouse={inHouse}
                                records={records}
                                onSearch={onSearch}
                                onSelectedRecord={onSelectedRecord}
                                getCustomerColumns={getCustomerColumns}
                                loadingCustomerInfo={loadingCustomerInfo}
                                showAddNewCustomerButton={showAddNewCustomerButton}
                                onClickNewCustomerButton={onClickNewCustomerButton}
                            />
                            <div className={classes.main}>
                                <Header className={classes.customerSection}>
                                    <div className={classes.scanner}>
                                        <InputSearch
                                            key={searchData.key}
                                            initialSearch={searchData.search}
                                            disabled={!selectedRecord}
                                            customClasses={clsx(search)}
                                            onSearch={onSearchVehicle}
                                            placeholder="Enter text to search vehicle"
                                        />
                                        {selectedRecord && (
                                            <VinScanner
                                                scanStock
                                                shortButton
                                                loading={loadingVehicleInfo}
                                                onSearchVehicle={onSearchVehicle}
                                            />
                                        )}
                                    </div>
                                    {!inHouse && (
                                        <Button
                                            startIcon={<AddIcon />}
                                            size="small"
                                            onClick={() => onClickNewVehicleButton(selectedRecord)}
                                            disabled={!selectedRecord}
                                            variant="outlined"
                                            color="secondary"
                                        >
                                            Add new Vehicle
                                        </Button>
                                    ) }
                                </Header>
                                {isMobile && (
                                    <VehiclesInCards
                                        classes={classes}
                                        records={vehicles ?? []}
                                        onSelect={(record) => onSelectCustomer({
                                            customer: selectedRecord,
                                            vehicle: record,
                                        })}
                                        inHouse={inHouse}
                                        loading={loadingVehicleInfo}
                                    />
                                )}
                                {!isMobile && (
                                    <div className={classes.mainTable}>
                                        <Table
                                            cursor="default"
                                            load={loadingVehicleInfo}
                                            totalvehicles={vehicles.length || 0}
                                            data={loadingVehicleInfo ? [] : vehicles}
                                            columns={getVehicleColumns()}
                                            className="-highlight"
                                            rowSelected
                                            enableRowHover
                                            getTrProps={(_, rowInfo) => ({
                                                onDoubleClick: () => {
                                                    onSelectedVehicle(rowInfo.original);
                                                },
                                                onClick: () => {
                                                    onSelectedVehicle(rowInfo.original);
                                                },
                                            })}
                                        />
                                    </div>
                                )}
                            </div>
                        </Split>
                    ) : (
                        <CustomerPanel
                            isMobile={isMobile}
                            search={search}
                            classes={classes}
                            inHouse={inHouse}
                            records={records}
                            onSearch={onSearch}
                            onSelectedRecord={onSelectedRecord}
                            getCustomerColumns={getCustomerColumns}
                            loadingCustomerInfo={loadingCustomerInfo}
                            showAddNewCustomerButton={showAddNewCustomerButton}
                            onClickNewCustomerButton={onClickNewCustomerButton}
                        />
                    )}
                </DialogContent>
                {!isMobile && (
                    <DialogActions
                        titlePrimary="Ok"
                        onClickSecondary={onClose}
                        onClickPrimary={() => onSelectCustomer({
                            customer: selectedRecord,
                            vehicle: selectedVehicle,
                        })}
                        disablePrimaryButton={!selectedRecord}
                    />
                )}
            </Dialog>
        </>
    );
};

CustomerForServiceList.propTypes = {
    inHouse: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSelectCustomer: PropTypes.func.isRequired,
    showAddNewCustomerButton: PropTypes.bool,
    onClickNewCustomerButton: PropTypes.func,
    onClickNewVehicleButton: PropTypes.func,
    showVehicleGrid: PropTypes.bool,
};

CustomerForServiceList.defaultProps = {
    inHouse: false,
    showAddNewCustomerButton: false,
    onClickNewCustomerButton: () => {},
    onClickNewVehicleButton: () => {},
    showVehicleGrid: true,
};

export default CustomerForServiceList;
